import React, { useState, useEffect, useMemo } from 'react';

import { FaSearch, FaHistory, FaBoxes, FaPrint, FaExclamationTriangle } from 'react-icons/fa';
import { fuzzySearch } from '../../utils/fuzzySearch';
import styles from './styles/StockManager.module.css';
import PrimaryButton from '../PrimaryButton';
import { useAuth } from '../../context/AuthContext';
import { nombreOperativo } from '../../utils/productName';
import Pagination, { EMPTY_PAGINATION, PaginationMeta, readPaginated } from './Pagination';
import StockFilters, { EMPTY_STOCK_FILTERS, StockFilterValues } from './StockFilters';

interface Product {
    idProductos: number;
    NombresProductos: string;
    NombreEtiqueta?: string | null;
    es_promocion?: boolean | number;
}

interface StockReportItem {
    fecha: string;
    idProductos?: number;
    producto: string;
    subcategoria?: string;
    stock_inicial: number;
    entradas_dia: number;
    salidas_dia: number;
    stock_final: number;
    salidas_futuras: number;
    stock_disponible: number;
    stock_minimo: number;
    semanas_stock: number;
    puesta_cero_pendiente?: boolean;
}

interface StockMovement {
    id: number;
    producto: string;
    fecha: string;
    tipo: string;
    cantidad: number;
    motivo: string;
    usuario: string;
    id_local?: number; // Optional in display
    pedido_id?: string | null;     // UUID completo del pedido (solo ventas)
    pedido_numero?: number | null; // Numero de pedido legible (id_pedido_app)
}

interface Local {
    locales_id: number;
    nombre_local: string;
}

const StockManager: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [report, setReport] = useState<StockReportItem[]>([]);
    // Paginacion independiente por vista: cada una lista un conjunto distinto.
    const [perPage, setPerPage] = useState(50);
    const [reportPage, setReportPage] = useState(1);
    const [reportMeta, setReportMeta] = useState<PaginationMeta>(EMPTY_PAGINATION);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyMeta, setHistoryMeta] = useState<PaginationMeta>(EMPTY_PAGINATION);
    const [faltantePage, setFaltantePage] = useState(1);
    const [faltanteMeta, setFaltanteMeta] = useState<PaginationMeta>(EMPTY_PAGINATION);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState({ type: '', text: '' });
    const [view, setView] = useState<'input' | 'report' | 'history' | 'faltante'>('report');
    const [history, setHistory] = useState<StockMovement[]>([]);

    // Faltante state
    const [faltanteReport, setFaltanteReport] = useState<{
        producto: string; subcategoria: string;
        stock_disponible: number; stock_minimo: number; faltante: number;
    }[]>([]);
    const [faltanteLoading, setFaltanteLoading] = useState(false);
    const [faltanteSearch, setFaltanteSearch] = useState('');
    const [faltanteSort, setFaltanteSort] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'faltante', direction: 'desc' });

    // Form State
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('');
    const [quantity, setQuantity] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]); // Default today
    const [movementType, setMovementType] = useState('entrada');
    const [reason, setReason] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [historySearchTerm, setHistorySearchTerm] = useState('');
    const [showSales, setShowSales] = useState(false);

    // Location State
    const [locales, setLocales] = useState<Local[]>([]);
    const [selectedLocal, setSelectedLocal] = useState<number | null>(null); // null = Global
    /*
     * Filtros combinables. 'filters' es lo que el usuario esta editando;
     * 'appliedFilters' es lo que realmente se esta mostrando. Separarlos evita
     * refetchear en cada tecla y replica el comportamiento de Pedidos
     * (editar -> Filtrar -> aplicar).
     */
    const [filters, setFilters] = useState<StockFilterValues>(EMPTY_STOCK_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState<StockFilterValues>(EMPTY_STOCK_FILTERS);
    const [formLocal, setFormLocal] = useState<number | null>(null); // For the input form specifically

    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Modal de confirmación para poner en 0 / revertir
    const [zeroModal, setZeroModal] = useState<{
        action: 'cero' | 'revertir';
        item: StockReportItem;
    } | null>(null);
    const [zeroProcessing, setZeroProcessing] = useState(false);

    const { user, rol, defaultLocalId } = useAuth();
    const canSeeInputHistory = rol === 'it' || rol === 'admin';

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    const fetchProducts = async () => {
        try {
            const res = await fetch(`${API_URL}/productos`);
            const data = await res.json();
            if (Array.isArray(data)) {
                setProducts(data.filter((product: Product) => Number(product.es_promocion) !== 1));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchLocales = async () => {
        try {
            const res = await fetch(`${API_URL}/locales`);
            const data = await res.json();
            if (Array.isArray(data)) setLocales(data);
        } catch (err) {
            console.error(err);
        }
    };

    const applyFilters = () => {
        setAppliedFilters(filters);
        setReportPage(1);
        setHistoryPage(1);
        setFaltantePage(1);
    };

    /*
     * El chip de local se aplica al instante (se comportaba asi antes de
     * unificar los filtros); el resto de los criterios esperan al boton
     * "Filtrar". selectedLocal sigue siendo la fuente para las peticiones.
     */
    useEffect(() => {
        setSelectedLocal(filters.local);
        setAppliedFilters(prev => ({ ...prev, local: filters.local }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.local]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (selectedLocal) params.append('id_local', selectedLocal.toString());
            // Los filtros se resuelven en el backend para que la paginacion
            // devuelva paginas completas.
            if (appliedFilters.producto.trim()) params.append('producto', appliedFilters.producto.trim());
            if (appliedFilters.subcategoria !== 'Todas') params.append('subcategoria', appliedFilters.subcategoria);
            if (appliedFilters.estado !== 'todos') params.append('estado', appliedFilters.estado);
            params.append('page', String(reportPage));
            params.append('per_page', String(perPage));
            const res = await fetch(`${API_URL}/admin/stock/report?${params.toString()}`);
            if (res.ok) {
                const { items, pagination } = readPaginated<StockReportItem>(await res.json());
                setReport(items);
                setReportMeta(pagination);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async (searchStr: string = historySearchTerm, withSales: boolean = showSales) => {
        setLoading(true);
        try {
            let url = `${API_URL}/admin/stock/movements`;
            const params = new URLSearchParams();
            if (selectedLocal) params.append('id_local', selectedLocal.toString());
            if (searchStr.trim()) params.append('producto', searchStr.trim());
            if (withSales) params.append('include_sales', 'true');

            params.append('page', String(historyPage));
            params.append('per_page', String(perPage));
            url += `?${params.toString()}`;

            const res = await fetch(url);
            if (res.ok) {
                const { items, pagination } = readPaginated<any>(await res.json());
                setHistory(items);
                setHistoryMeta(pagination);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const [subcats, setSubcats] = useState<Array<{ idSubcategoria: number; nombre: string }>>([]);
    const fetchSubcats = async () => {
        try {
            const res = await fetch(`${API_URL}/subcategorias`);
            const data = await res.json();
            if (Array.isArray(data)) setSubcats(data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchLocales();
        fetchSubcats();
        if (defaultLocalId) {
            setSelectedLocal(defaultLocalId);
            setFormLocal(defaultLocalId);
        }
    }, [defaultLocalId]);

    useEffect(() => {
        if (view === 'report') fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, selectedLocal, reportPage, perPage, appliedFilters]);

    // Cambiar de local, de busqueda o de tamano de pagina invalida la pagina
    // actual: se vuelve al inicio para no quedar en un offset inexistente.
    useEffect(() => {
        setReportPage(1);
        setHistoryPage(1);
        setFaltantePage(1);
    }, [selectedLocal, perPage, historySearchTerm, showSales]);

    useEffect(() => {
        if (view === 'history') {
            const delayDebounceFn = setTimeout(() => {
                fetchHistory(historySearchTerm, showSales);
            }, 400);
            return () => clearTimeout(delayDebounceFn);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, selectedLocal, historySearchTerm, showSales, historyPage, perPage]);

    const fetchFaltante = async () => {
        setFaltanteLoading(true);
        try {
            const params = new URLSearchParams();
            if (selectedLocal) params.append('id_local', selectedLocal.toString());
            params.append('page', String(faltantePage));
            params.append('per_page', String(perPage));
            const res = await fetch(`${API_URL}/admin/stock/faltante?${params.toString()}`);
            if (res.ok) {
                const { items, pagination } = readPaginated<any>(await res.json());
                setFaltanteReport(items);
                setFaltanteMeta(pagination);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setFaltanteLoading(false);
        }
    };

    useEffect(() => {
        if (view === 'faltante') fetchFaltante();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, selectedLocal, faltantePage, perPage]);

    const filteredProducts = useMemo(() => {
        return fuzzySearch(products, searchTerm, {
            keys: ['NombreEtiqueta', 'NombresProductos'],
            threshold: 0.35
        });
    }, [products, searchTerm]);

    const handleAddStock = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedProduct || !quantity) return;

        // Determine effective local for this movement
        const effectiveLocal = selectedLocal || formLocal;
        if (!effectiveLocal) {
            setMsg({ type: 'error', text: 'Por favor selecciona un local' });
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/admin/stock/movimiento`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_producto: selectedProduct,
                    tipo: movementType,
                    cantidad: quantity,
                    motivo: reason || (movementType === 'entrada' ? 'Entrada Manual' : 'Salida Manual'),
                    usuario: user || 'Admin',
                    fecha: date,
                    id_local: effectiveLocal
                })
            });

            if (res.ok) {
                setMsg({ type: 'success', text: 'Stock actualizado correctamente' });
                setQuantity('');
                setSearchTerm(''); // Clear search
                setReason('');
                setMovementType('entrada'); // Reset to default
                fetchReport();
            } else {
                setMsg({ type: 'error', text: 'Error al agregar stock' });
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Error de conexión' });
        } finally {
            setLoading(false);
            setTimeout(() => setMsg({ type: '', text: '' }), 3000);
        }
    };

    const confirmZeroAction = async () => {
        if (!zeroModal || !selectedLocal) return;
        const { action, item } = zeroModal;
        if (!item.idProductos) {
            setMsg({ type: 'error', text: 'No se pudo identificar el producto.' });
            setZeroModal(null);
            return;
        }

        setZeroProcessing(true);
        try {
            const endpoint = action === 'cero' ? 'poner-cero' : 'revertir-cero';
            const res = await fetch(`${API_URL}/admin/stock/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_producto: item.idProductos,
                    id_local: selectedLocal,
                    usuario: user || 'Admin'
                })
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setMsg({
                    type: 'success',
                    text: action === 'cero'
                        ? `Stock de "${item.producto}" puesto en 0 en este local.`
                        : `Stock de "${item.producto}" restaurado en este local.`
                });
                setZeroModal(null);
                fetchReport();
            } else {
                setMsg({ type: 'error', text: data.error || 'Ocurrió un error.' });
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Error de conexión' });
        } finally {
            setZeroProcessing(false);
            setTimeout(() => setMsg({ type: '', text: '' }), 4000);
        }
    };

    // Table State
    const [tableSearchTerm, setTableSearchTerm] = useState('');
    const [subcatFilter, setSubcatFilter] = useState('');
    const [sortConfig, setSortConfig] = useState<{ key: keyof StockReportItem; direction: 'asc' | 'desc' } | null>({ key: 'producto', direction: 'asc' });

    const handleSort = (key: keyof StockReportItem) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedReport = useMemo(() => {
        let sortableItems = [...report];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                const aValue = a[sortConfig.key] ?? '';
                const bValue = b[sortConfig.key] ?? '';
                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [report, sortConfig]);

    const filteredAndSortedReport = useMemo(() => {
        const filtered = fuzzySearch(sortedReport, tableSearchTerm, {
            keys: ['producto'],
            threshold: 0.35
        });
        return filtered.filter(item =>
            subcatFilter === '' || (item.subcategoria || '') === subcatFilter
        );
    }, [sortedReport, tableSearchTerm, subcatFilter]);

    const getSortIndicator = (current: { key: string; direction: 'asc' | 'desc' } | null, key: string) => {
        if (!current || current.key !== key) return ' ↕';
        return current.direction === 'asc' ? ' ▲' : ' ▼';
    };

    return (
        <div className={styles['stock-container']}>
            <h3>Gestión de Stock</h3>

            {/*
              * Filtros combinables: conservan los chips de local propios de
              * Stock y suman la logica multi-criterio de Pedidos.
              */}
            <StockFilters
                values={filters}
                onChange={setFilters}
                onApply={applyFilters}
                locales={locales}
                subcategorias={subcats}
                showEstado={view === 'report'}
            />

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #ccc', paddingBottom: '10px' }}>
                <button
                    onClick={() => setView('report')}
                    style={{
                        flex: 1, padding: '10px', border: 'none', borderRadius: '5px', cursor: 'pointer',
                        backgroundColor: view === 'report' ? '#e3f2fd' : 'transparent', fontWeight: 'bold', color: view === 'report' ? '#0277bd' : '#666'
                    }}
                >
                    <FaSearch style={{ marginRight: '5px' }} /> Stock Actual
                </button>
                {canSeeInputHistory && (
                    <button
                        onClick={() => setView('input')}
                        style={{
                            flex: 1, padding: '10px', border: 'none', borderRadius: '5px', cursor: 'pointer',
                            backgroundColor: view === 'input' ? '#e3f2fd' : 'transparent', fontWeight: 'bold', color: view === 'input' ? '#0277bd' : '#666'
                        }}
                    >
                        <FaBoxes style={{ marginRight: '5px' }} /> Entrada / Salida
                    </button>
                )}
                {canSeeInputHistory && (
                    <button
                        onClick={() => setView('history')}
                        style={{
                            flex: 1, padding: '10px', border: 'none', borderRadius: '5px', cursor: 'pointer',
                            backgroundColor: view === 'history' ? '#e3f2fd' : 'transparent', fontWeight: 'bold', color: view === 'history' ? '#0277bd' : '#666'
                        }}
                    >
                        <FaHistory style={{ marginRight: '5px' }} /> Historial
                    </button>
                )}
                <button
                    onClick={() => setView('faltante')}
                    style={{
                        flex: 1, padding: '10px', border: 'none', borderRadius: '5px', cursor: 'pointer',
                        backgroundColor: view === 'faltante' ? '#fff3e0' : 'transparent', fontWeight: 'bold', color: view === 'faltante' ? '#e65100' : '#666'
                    }}
                >
                    <FaExclamationTriangle style={{ marginRight: '5px' }} /> Faltantes
                </button>
            </div>

            {error && <div className={styles['error-msg']} style={{ marginBottom: '15px' }}>{error}</div>}
            {success && <div className={styles['success-msg']} style={{ marginBottom: '15px' }}>{success}</div>}

            {/* Mensaje global de acciones (poner en 0 / revertir, visible en cualquier vista) */}
            {view !== 'input' && msg.text && (
                <div className={msg.type === 'error' ? styles['error-msg'] : styles['success-msg']} style={{ marginBottom: '15px' }}>
                    {msg.text}
                </div>
            )}

            {view === 'input' && (
                <div className={styles['stock-form']}>
                    <h4>Entrada / Salida de Stock</h4>
                    <form onSubmit={handleAddStock}>
                        <div className={styles['form-row']}>
                            <div className={styles['form-group']}>
                                <label>Tipo</label>
                                <select
                                    value={movementType}
                                    onChange={(e) => {
                                        setMovementType(e.target.value);
                                        if (e.target.value === 'salida') setReason('Ajuste Inventario');
                                        else setReason('');
                                    }}
                                    className={movementType === 'salida' ? styles['select-salida'] : styles['select-entrada']}
                                >
                                    <option value="entrada">Entrada (+)</option>
                                    <option value="salida">Salida (-)</option>
                                </select>
                            </div>

                            {/* Campo ancho: el buscador necesita espacio para el desplegable */}
                            <div
                                className={`${styles['form-group']} ${styles['form-group-wide']}`}
                                style={{ position: 'relative' }}
                            >
                                <label>Producto</label>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setShowSuggestions(true);
                                        if (!e.target.value) setSelectedProduct('');
                                    }}
                                    onFocus={() => setShowSuggestions(true)}
                                    placeholder="Buscar producto..."
                                    autoComplete="off"
                                    className={styles['search-input']}
                                />
                                {showSuggestions && searchTerm && filteredProducts.length > 0 && (
                                    <ul className={styles['suggestions-list']}>
                                        {filteredProducts.map(p => (
                                            <li
                                                key={p.idProductos}
                                                onClick={() => {
                                                    setSearchTerm(nombreOperativo(p));
                                                    setSelectedProduct(p.idProductos.toString());
                                                    setShowSuggestions(false);
                                                }}
                                                className={styles['suggestion-item']}
                                            >
                                                {nombreOperativo(p)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className={styles['form-group']}>
                                <label>Cantidad</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    required
                                />
                            </div>
                            <div className={styles['form-group']}>
                                <label>Fecha</label>
                                <input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    required
                                />
                            </div>

                            {movementType === 'salida' && (
                                <div className={styles['form-group']}>
                                    <label>Motivo</label>
                                    <select
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        required={movementType === 'salida'}
                                    >
                                        <option value="">Seleccionar...</option>
                                        <option value="Venta Manual">Venta Manual</option>
                                        <option value="Avería/Desperdicio">Avería/Desperdicio</option>
                                        <option value="Consumo Interno">Consumo Interno</option>
                                        <option value="Ajuste Inventario">Ajuste Inventario</option>
                                        <option value="Otro">Otro</option>
                                    </select>
                                </div>
                            )}

                            <div className={styles['form-group']}>
                                <PrimaryButton type="submit" className={movementType === 'salida' ? styles['btn-salida'] : ''}>
                                    {movementType === 'entrada' ? 'Cargar' : 'Registrar'}
                                </PrimaryButton>
                            </div>
                        </div>

                        {selectedLocal === null && (
                            <div className={styles['form-row']} style={{ marginTop: '10px' }}>
                                <div className={styles['form-group']}>
                                    <label>Seleccionar Local:</label>
                                    <select
                                        value={formLocal || ''}
                                        onChange={(e) => setFormLocal(Number(e.target.value))}
                                        required
                                    >
                                        <option value="">-- Seleccionar --</option>
                                        {locales.map(l => (
                                            <option key={l.locales_id} value={l.locales_id}>
                                                {l.nombre_local}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                    </form>
                    {msg.text && (
                        <div className={msg.type === 'error' ? styles['error-msg'] : styles['success-msg']}>
                            {msg.text}
                        </div>
                    )}
                </div>
            )}

            {view === 'report' && (
                <div>
                    <div className={styles['report-controls']}>
                        <h4>Resumen de Stock</h4>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                placeholder="Filtrar por nombre..."
                                value={tableSearchTerm}
                                onChange={(e) => setTableSearchTerm(e.target.value)}
                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                            />
                            <select
                                value={subcatFilter}
                                onChange={(e) => setSubcatFilter(e.target.value)}
                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                            >
                                <option value="">Todas las subcategorías</option>
                                {subcats.map(s => (
                                    <option key={s.idSubcategoria} value={s.nombre}>{s.nombre}</option>
                                ))}
                            </select>
                            <span style={{ fontSize: '0.8rem', color: '#666' }}>{filteredAndSortedReport.length} items</span>
                        </div>
                    </div>

                    <div className={styles['report-section']}>
                        <table className={styles['stock-table']}>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('producto')} style={{ cursor: 'pointer' }}>Producto{getSortIndicator(sortConfig, 'producto')}</th>
                                    <th onClick={() => handleSort('stock_inicial')} style={{ cursor: 'pointer' }}>Inicial{getSortIndicator(sortConfig, 'stock_inicial')}</th>
                                    <th onClick={() => handleSort('entradas_dia')} style={{ cursor: 'pointer' }}>Entradas{getSortIndicator(sortConfig, 'entradas_dia')}</th>
                                    <th onClick={() => handleSort('salidas_dia')} style={{ cursor: 'pointer' }}>Salidas{getSortIndicator(sortConfig, 'salidas_dia')}</th>
                                    <th onClick={() => handleSort('stock_final')} style={{ cursor: 'pointer' }}>Final{getSortIndicator(sortConfig, 'stock_final')}</th>
                                    <th onClick={() => handleSort('salidas_futuras')} style={{ cursor: 'pointer' }}>Pend.{getSortIndicator(sortConfig, 'salidas_futuras')}</th>
                                    <th onClick={() => handleSort('stock_disponible')} style={{ cursor: 'pointer' }}>Disponible{getSortIndicator(sortConfig, 'stock_disponible')}</th>
                                    <th onClick={() => handleSort('stock_minimo')} style={{ cursor: 'pointer' }}>Min.{getSortIndicator(sortConfig, 'stock_minimo')}</th>
                                    <th onClick={() => handleSort('semanas_stock')} style={{ cursor: 'pointer' }}>Sems.{getSortIndicator(sortConfig, 'semanas_stock')}</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAndSortedReport.map((item, idx) => (
                                    <tr key={idx}>
                                        <td style={{ textAlign: 'left' }}>{item.producto}</td>
                                        <td>{item.stock_inicial}</td>
                                        <td>{item.entradas_dia}</td>
                                        <td>{item.salidas_dia}</td>
                                        <td>{item.stock_final}</td>
                                        <td>{item.salidas_futuras}</td>
                                        <td style={{
                                            backgroundColor: item.stock_disponible <= 0 ? '#ffcdd2' : item.stock_disponible <= item.stock_minimo ? '#fff9c4' : '#c8e6c9',
                                            fontWeight: 'bold'
                                        }}>
                                            {item.stock_disponible}
                                        </td>
                                        <td>{item.stock_minimo}</td>
                                        <td>{item.semanas_stock}</td>
                                        <td>
                                            {selectedLocal === null ? (
                                                <span style={{ fontSize: '0.75rem', color: '#999' }} title="Elegí un local para poder modificar su stock">
                                                    Elegí un local
                                                </span>
                                            ) : item.puesta_cero_pendiente ? (
                                                <button
                                                    onClick={() => setZeroModal({ action: 'revertir', item })}
                                                    title="Revertir la puesta a 0 y restaurar el stock"
                                                    style={{
                                                        padding: '5px 10px', border: 'none', borderRadius: '5px',
                                                        cursor: 'pointer', backgroundColor: '#2e7d32', color: 'white',
                                                        fontWeight: 'bold', fontSize: '0.78rem', whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    ↩ Revertir
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => setZeroModal({ action: 'cero', item })}
                                                    disabled={item.stock_final <= 0}
                                                    title={item.stock_final <= 0 ? 'El stock ya es 0 o negativo' : 'Poner el stock de este producto en 0 (solo este local)'}
                                                    style={{
                                                        padding: '5px 10px', border: 'none', borderRadius: '5px',
                                                        cursor: item.stock_final <= 0 ? 'not-allowed' : 'pointer',
                                                        backgroundColor: item.stock_final <= 0 ? '#e0e0e0' : '#c62828',
                                                        color: item.stock_final <= 0 ? '#999' : 'white',
                                                        fontWeight: 'bold', fontSize: '0.78rem', whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    Poner en 0
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        meta={reportMeta}
                        onPageChange={setReportPage}
                        onPerPageChange={setPerPage}
                        itemLabel="productos"
                    />
                </div>
            )}

            {view === 'history' && (
                <div className={styles['history-section']}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <h4 style={{ margin: 0 }}>Historial de Movimientos</h4>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ position: 'relative' }}>
                                <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none' }} />
                                <input
                                    type="text"
                                    placeholder="Buscar producto..."
                                    value={historySearchTerm}
                                    onChange={(e) => setHistorySearchTerm(e.target.value)}
                                    style={{ padding: '8px 8px 8px 32px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.9rem', minWidth: '200px' }}
                                />
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <input type="checkbox" checked={showSales} onChange={(e) => setShowSales(e.target.checked)} />
                                Ventas
                            </label>
                        </div>
                    </div>
                    <table className={styles['stock-table']}>
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Producto</th>
                                <th>Tipo</th>
                                <th>Cant.</th>
                                <th>Motivo</th>
                                <th>Usuario</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fuzzySearch(history, historySearchTerm, { keys: ['producto'], threshold: 0.4 }).map(h => (
                                <tr key={h.id}>
                                    <td>{new Date(h.fecha).toLocaleString()}</td>
                                    <td>{h.producto}</td>
                                    <td style={{ color: h.tipo === 'entrada' ? 'green' : 'red', fontWeight: 'bold' }}>{h.tipo.toUpperCase()}</td>
                                    <td>{h.cantidad}</td>
                                    <td title={h.pedido_id || undefined}>
                                        {(() => {
                                            // Es un movimiento de venta si el motivo arranca con
                                            // 'Venta Pedido ' o 'Venta Promo '. En ese caso el motivo
                                            // guarda el order_id (UUID completo en registros nuevos,
                                            // 8 hex en los viejos) que aca quitamos para mostrarlo
                                            // limpio ('Venta Pedido' / 'Venta Promo') + el dato resuelto.
                                            const ventaMatch = h.motivo.match(/^(Venta (?:Pedido|Promo))\b/);
                                            if (!ventaMatch) return h.motivo; // Ajustes, entradas manuales, etc.
                                            const etiqueta = ventaMatch[1];
                                            if (h.pedido_numero) return `${etiqueta} #${h.pedido_numero}`;
                                            if (h.pedido_id) return `${etiqueta} ${h.pedido_id}`;
                                            // Pedido borrado: no se pudo resolver numero ni UUID.
                                            return `${h.motivo} (eliminado)`;
                                        })()}
                                    </td>
                                    <td>{h.usuario}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Pagination
                        meta={historyMeta}
                        onPageChange={setHistoryPage}
                        onPerPageChange={setPerPage}
                        itemLabel="movimientos"
                    />
                </div>
            )}

            {view === 'faltante' && (() => {
                const localNombre = 'Global';
                const filteredFaltante = [...faltanteReport]
                    .filter(i => faltanteSearch === '' ||
                        i.producto.toLowerCase().includes(faltanteSearch.toLowerCase()) ||
                        (i.subcategoria || '').toLowerCase().includes(faltanteSearch.toLowerCase()))
                    .sort((a, b) => {
                        const aVal = (a as any)[faltanteSort.key];
                        const bVal = (b as any)[faltanteSort.key];
                        if (aVal < bVal) return faltanteSort.direction === 'asc' ? -1 : 1;
                        if (aVal > bVal) return faltanteSort.direction === 'asc' ? 1 : -1;
                        return 0;
                    });
                const toggleSort = (key: string) =>
                    setFaltanteSort(s => ({ key, direction: s.key === key && s.direction === 'asc' ? 'desc' : 'asc' }));

                return (
                    <div>
                        {/* Barra única: título + buscador + acciones */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                            <div>
                                <h3 style={{ margin: 0, color: '#e65100' }}>
                                    Faltantes — {localNombre}
                                </h3>
                                <p style={{ margin: '3px 0 0', fontSize: '0.82em', color: '#aaa' }}>
                                    {faltanteLoading ? 'Calculando...' : `${filteredFaltante.length} producto(s) por debajo del mínimo`}
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <div style={{ position: 'relative' }}>
                                    <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none' }} />
                                    <input
                                        type="text"
                                        placeholder="Buscar producto..."
                                        value={faltanteSearch}
                                        onChange={e => setFaltanteSearch(e.target.value)}
                                        style={{ padding: '8px 8px 8px 32px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.9rem', minWidth: '200px' }}
                                    />
                                </div>
                                <button
                                    onClick={fetchFaltante}
                                    style={{ padding: '8px 14px', backgroundColor: '#e65100', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                    🔄 Actualizar
                                </button>
                                <button
                                    onClick={() => {
                                        const printContent = document.getElementById('faltante-stock-print');
                                        if (!printContent) return;
                                        const win = window.open('', '_blank', 'width=800,height=600');
                                        if (!win) return;
                                        win.document.write(`<html><head><title>Faltantes - ${localNombre}</title>
                                            <style>body{font-family:Arial,sans-serif;padding:20px;color:#000}h2{margin-bottom:4px}
                                            table{width:100%;border-collapse:collapse}th{background:#e65100;color:white;padding:8px 12px;text-align:left}
                                            td{padding:7px 12px;border-bottom:1px solid #ddd}tr:nth-child(even) td{background:#fafafa}
                                            .footer{margin-top:20px;font-size:0.8em;color:#999}</style></head><body>
                                            <h2>Faltantes — ${localNombre}</h2>
                                            ${printContent.outerHTML}
                                            <p class="footer">Generado: ${new Date().toLocaleString('es-AR')}</p></body></html>`);
                                        win.document.close();
                                        win.focus();
                                        setTimeout(() => { win.print(); win.close(); }, 400);
                                    }}
                                    style={{ padding: '8px 14px', backgroundColor: '#37474f', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}
                                >
                                    <FaPrint /> Imprimir
                                </button>
                            </div>
                        </div>

                        {faltanteLoading ? (
                            <p style={{ color: '#aaa' }}>Calculando...</p>
                        ) : faltanteReport.length === 0 ? (
                            <p style={{ color: '#27ae60', fontWeight: 'bold' }}>✅ Todo el stock está al día.</p>
                        ) : (
                            <div className={styles['report-section']} id="faltante-stock-print">
                                <table className={styles['stock-table']}>
                                    <thead>
                                        <tr>
                                            <th onClick={() => toggleSort('producto')} style={{ cursor: 'pointer', textAlign: 'left' }}>Producto {getSortIndicator(faltanteSort, 'producto')}</th>
                                            <th onClick={() => toggleSort('subcategoria')} style={{ cursor: 'pointer' }}>Subcategoría {getSortIndicator(faltanteSort, 'subcategoria')}</th>
                                            <th onClick={() => toggleSort('stock_disponible')} style={{ cursor: 'pointer' }}>Stock Actual {getSortIndicator(faltanteSort, 'stock_disponible')}</th>
                                            <th onClick={() => toggleSort('stock_minimo')} style={{ cursor: 'pointer' }}>Mínimo (7d) {getSortIndicator(faltanteSort, 'stock_minimo')}</th>
                                            <th onClick={() => toggleSort('faltante')} style={{ cursor: 'pointer' }}>Faltan {getSortIndicator(faltanteSort, 'faltante')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredFaltante.map((item, idx) => (
                                            <tr key={idx}>
                                                <td style={{ textAlign: 'left' }}>{item.producto}</td>
                                                <td>{item.subcategoria}</td>
                                                <td>{item.stock_disponible}</td>
                                                <td>{item.stock_minimo}</td>
                                                <td style={{ fontWeight: 'bold', color: '#c62828' }}>{item.faltante}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <Pagination
                            meta={faltanteMeta}
                            onPageChange={setFaltantePage}
                            onPerPageChange={setPerPage}
                            itemLabel="faltantes"
                        />
                    </div>
                );
            })()}

            {/* Modal de confirmación: Poner en 0 / Revertir */}
            {zeroModal && (
                <div
                    onClick={() => !zeroProcessing && setZeroModal(null)}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', zIndex: 1000
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white', borderRadius: '10px', padding: '24px',
                            maxWidth: '440px', width: '90%', boxShadow: '0 8px 30px rgba(0,0,0,0.25)'
                        }}
                    >
                        {zeroModal.action === 'cero' ? (
                            <>
                                <h3 style={{ marginTop: 0, color: '#c62828' }}>⚠️ Poner stock en 0</h3>
                                <p style={{ color: '#333', lineHeight: 1.5 }}>
                                    Vas a poner en <strong>0</strong> el stock de:
                                </p>
                                <p style={{ fontWeight: 'bold', fontSize: '1.05rem', margin: '8px 0' }}>
                                    {zeroModal.item.producto}
                                </p>
                                <p style={{ color: '#555', fontSize: '0.9rem' }}>
                                    Local: <strong>{locales.find(l => l.locales_id === selectedLocal)?.nombre_local || `#${selectedLocal}`}</strong><br />
                                    Stock actual: <strong>{zeroModal.item.stock_final}</strong> unidades<br />
                                    Esta acción afecta <strong>solo este local</strong>.
                                </p>
                                <p style={{ color: '#2e7d32', fontSize: '0.85rem' }}>
                                    Si fue un error, vas a poder revertirlo desde la misma fila.
                                </p>
                            </>
                        ) : (
                            <>
                                <h3 style={{ marginTop: 0, color: '#2e7d32' }}>↩ Revertir puesta a 0</h3>
                                <p style={{ color: '#333', lineHeight: 1.5 }}>
                                    Vas a restaurar el stock que se había puesto en 0 para:
                                </p>
                                <p style={{ fontWeight: 'bold', fontSize: '1.05rem', margin: '8px 0' }}>
                                    {zeroModal.item.producto}
                                </p>
                                <p style={{ color: '#555', fontSize: '0.9rem' }}>
                                    Local: <strong>{locales.find(l => l.locales_id === selectedLocal)?.nombre_local || `#${selectedLocal}`}</strong><br />
                                    Se devolverá la cantidad que se había descontado.
                                </p>
                            </>
                        )}

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button
                                onClick={() => setZeroModal(null)}
                                disabled={zeroProcessing}
                                style={{
                                    padding: '9px 18px', border: '1px solid #ccc', borderRadius: '6px',
                                    backgroundColor: 'white', cursor: 'pointer', fontWeight: 'bold'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmZeroAction}
                                disabled={zeroProcessing}
                                style={{
                                    padding: '9px 18px', border: 'none', borderRadius: '6px',
                                    backgroundColor: zeroModal.action === 'cero' ? '#c62828' : '#2e7d32',
                                    color: 'white', cursor: zeroProcessing ? 'wait' : 'pointer', fontWeight: 'bold'
                                }}
                            >
                                {zeroProcessing
                                    ? 'Procesando...'
                                    : zeroModal.action === 'cero' ? 'Sí, poner en 0' : 'Sí, revertir'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default StockManager;
